import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pauseSubscription, resumeSubscription } from '../../store/slices/subscriptionSlice';

const SubscriptionDetailModal = ({ show, onHide, subscription, metalPrices }) => {
  // Use metalPrices from props instead of fetching internally
  const currentMetalPrices = metalPrices;
  // Keep loading state for backward compatibility
  const [loadingPrices, setLoadingPrices] = useState(false);

  // Redux dispatch and selectors - ALL HOOKS MUST BE AT THE TOP
  const dispatch = useDispatch();
  const selectedSubscription = useSelector(state => state.subscriptions.selectedSubscription);
  const subscriptions = useSelector(state => state.subscriptions.subscriptions);

  // State for action loading and messages
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // State for confirmation modals
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);

  // Handler functions for pause and cancel
  const handlePauseClick = () => {
    setShowPauseConfirm(true);
  };


  const handleConfirmClose = () => {
    setShowPauseConfirm(false);
  };

  // Handle pause subscription with Redux
  const handlePauseConfirm = async () => {
    setLoading(true);
    setActionType('pause');
    setError(null);
    setSuccess(null);
    setShowPauseConfirm(false);

    try {
      await dispatch(pauseSubscription(subscription.id)).unwrap();
      setSuccess('Subscription paused successfully.');
    } catch (err) {
      setError(err.message || 'Failed to pause subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  if (!subscription || !show) return null;

  // Use updated subscription from Redux state if available, otherwise use prop
  // First check if we have this subscription in the Redux subscriptions list (most up-to-date)
  const reduxSubscription = subscriptions.find(sub => sub.id === subscription.id);

  // Use Redux subscription if found, otherwise fall back to selectedSubscription, then prop
  const currentSubscription = reduxSubscription ||
    (selectedSubscription && selectedSubscription.id === subscription.id ? selectedSubscription : subscription);

  const {
    plan_name,
    metal_type,
    amount_per_period, // Dollar amount
    weight, // Physical amount
    weight_type = 'oz', // Default to oz if not specified
    period,
    price_per_period,
    current_balance, // Dollar amount
    status,
    next_billing_date,
    created_at
  } = currentSubscription;

  // Weight conversion function
  const convertWeight = (value, fromUnit, toUnit) => {
    const ozToGrams = 28.3495;
    const ozToKg = ozToGrams / 1000;

    // Convert to ounces first
    let valueInOz;
    switch (fromUnit) {
      case 'g':
        valueInOz = value / ozToGrams;
        break;
      case 'kg':
        valueInOz = value / ozToKg;
        break;
      case 'oz':
      default:
        valueInOz = value;
        break;
    }

    // Convert from ounces to target unit
    switch (toUnit) {
      case 'g':
        return valueInOz * ozToGrams;
      case 'kg':
        return valueInOz * ozToKg;
      case 'oz':
      default:
        return valueInOz;
    }
  };

  // Calculate progress towards delivery target
  const calculateProgress = () => {
    // Handle backward compatibility - extract weight from amount_per_period if needed
    let targetWeight = weight;
    let targetWeightType = weight_type;

    // If weight is not provided but amount_per_period contains weight info (old format)
    if (!weight && typeof amount_per_period === 'string' && amount_per_period.includes('oz')) {
      const match = amount_per_period.match(/(\d+(?:\.\d+)?)\s*oz/);
      if (match) {
        targetWeight = parseFloat(match[1]);
        targetWeightType = 'oz';
      }
    }

    // ROBUST minimum weight validation to prevent unrealistic targets
    if (targetWeight && targetWeightType) {
      let minimumWeight;

      // Set minimum weight for precious metals (gold, silver, platinum) - no artificial minimums
      // Allow any positive weight value
      minimumWeight = 0.001; // Very small minimum to prevent zero/negative values

      if (targetWeight < minimumWeight) {
        console.warn(`Target weight ${targetWeight} ${targetWeightType} is very small for ${metal_type}, setting to minimum: ${minimumWeight} ${targetWeightType}`);
        targetWeight = minimumWeight;
      }
    }

    // Handle current_balance - could be dollars (number) or weight string (old format)
    let balanceValue = current_balance;
    let balanceInOz = 0;

    if (typeof current_balance === 'string' && current_balance.includes('oz')) {
      // Old format: current_balance is weight (e.g., "850.0000 oz")
      const match = current_balance.match(/(\d+(?:\.\d+)?)\s*oz/);
      if (match) {
        balanceInOz = parseFloat(match[1]);
        // Convert to dollars if we have metal price
        if (currentMetalPrices && currentMetalPrices[metal_type] && currentMetalPrices[metal_type].price_per_ounce) {
          balanceValue = balanceInOz * currentMetalPrices[metal_type].price_per_ounce;
        }
      }
    } else if (typeof current_balance === 'number') {
      // New format: current_balance is dollars
      balanceValue = current_balance;
    }

    // Use metal prices if available for monetary target calculation
    if (currentMetalPrices && currentMetalPrices[metal_type] && targetWeight) {
      const priceData = currentMetalPrices[metal_type];

      // Use the correct price based on weight_type
      let pricePerUnit;
      if (targetWeightType === 'g') {
        pricePerUnit = priceData.price_per_gram;
      } else if (targetWeightType === 'kg') {
        pricePerUnit = priceData.price_per_gram * 1000; // Convert to price per kg
      } else {
        pricePerUnit = priceData.price_per_ounce; // Default to oz
      }

      if (!pricePerUnit) return { progressPercentage: 0, remaining: '0.00', unit: '$', targetValue: '0.00', currentWeight: 0, weightType: targetWeightType, monetaryValue: false };

      // Calculate monetary target: (weight * price per unit) + 15%
      const baseTarget = targetWeight * pricePerUnit;
      const targetPrice = baseTarget * 1.15; // Base target + 15%

      // Calculate progress as percentage of monetary value
      const progressPercentage = Math.max(0, Math.min(100, (balanceValue / targetPrice) * 100));

      // Calculate remaining amount in dollars
      const remainingValue = Math.max(0, targetPrice - balanceValue);

      // Calculate equivalent weight for display - how much metal can be bought with current balance
      const currentWeightAmount = balanceValue / pricePerUnit;

      return {
        progressPercentage: progressPercentage,
        remaining: remainingValue.toFixed(2),
        unit: '$',
        targetValue: targetPrice.toFixed(2),
        currentWeight: currentWeightAmount,
        weightType: targetWeightType,
        monetaryValue: true
      };
    }

    // No fallback - return zero progress if we can't calculate properly
    return {
      progressPercentage: 0,
      remaining: '0.00',
      unit: '$',
      targetValue: '0.00',
      currentWeight: 0,
      weightType: targetWeightType || 'oz',
      monetaryValue: false
    };
  };

  const progress = calculateProgress();
  const isMonetaryTarget = currentMetalPrices && currentMetalPrices[metal_type] && currentMetalPrices[metal_type].price_per_ounce;

  // Get status color (Tailwind classes)
  const getStatusColor = () => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format period for display
  const formatPeriod = (period) => {
    switch(period) {
      case 'weekly': return 'Weekly';
      case 'bi-weekly': return 'Every 2 Weeks';
      case 'monthly': return 'Monthly';
      case 'quarterly': return 'Every 3 Months';
      default: return period;
    }
  };

  return (
    <>
      {/* Main Modal */}
      {show && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onHide}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"></div>

            {/* Modal panel */}
            <div
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {plan_name || `${metal_type?.charAt(0).toUpperCase() + metal_type?.slice(1)} Subscription`}
                    </h3>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor()}`}>
                      {status?.charAt(0).toUpperCase() + status?.slice(1)}
                    </span>
                  </div>
                  <button
                    onClick={onHide}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="bg-white px-6 py-4">
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">{success}</p>
                  </div>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <div className="mb-3">
                      <span className="font-semibold text-gray-700">Amount per {period}:</span>
                      <span className="ml-2 text-gray-900">${amount_per_period}</span>
                    </div>
                    <div className="mb-3">
                      <span className="font-semibold text-gray-700">Target Weight:</span>
                      <span className="ml-2 text-gray-900">{weight} {weight_type}</span>
                    </div>
                  </div>
                  <div>
                    <div className="mb-3">
                      <span className="font-semibold text-gray-700">Next Billing Date:</span>
                      <span className="ml-2 text-gray-900">
                        {status === 'paused' ? 'N/A' : (next_billing_date ? (() => {
                          const date = new Date(next_billing_date);
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const year = date.getFullYear();
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          return `${month}/${day}/${year} ${hours}:${minutes}`;
                        })() : 'N/A')}
                      </span>
                    </div>
                    <div className="mb-3">
                      <span className="font-semibold text-gray-700">Created On:</span>
                      <span className="ml-2 text-gray-900">
                        {created_at ? (() => {
                          const date = new Date(created_at);
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const year = date.getFullYear();
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          return `${month}/${day}/${year} ${hours}:${minutes}`;
                        })() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Current Balance */}
                <div className="mb-6">
                  <div>
                    <span className="font-semibold text-gray-700">Current Balance:</span>
                    <span className="ml-2 text-gray-900">
                      ${current_balance?.toFixed ? current_balance.toFixed(2) : current_balance}
                    </span>
                    {progress.currentWeight !== undefined && (
                      <span className="ml-1 text-gray-500">
                        ({progress.currentWeight?.toFixed(4)} {progress.weightType})
                      </span>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-700">Progress</span>
                    {loadingPrices && (
                      <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-5 overflow-hidden">
                    <div
                      className={`h-full ${metal_type === 'gold' ? 'bg-yellow-500' : 'bg-blue-500'} transition-all duration-300`}
                      style={{ width: `${progress.progressPercentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-sm text-gray-500">{progress.progressPercentage?.toFixed(0)}%</span>
                    <span className="text-sm text-gray-500">
                      Approximately {progress.unit}{progress.remaining} to go
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    {status === 'active' && (
                      <button
                        onClick={handlePauseClick}
                        disabled={loading && actionType === 'pause'}
                        className="px-6 py-2 bg-yellow-500 text-white rounded-full font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {loading && actionType === 'pause' && (
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        {loading && actionType === 'pause' ? 'Pausing...' : 'Pause Subscription'}
                      </button>
                    )}

                    {status === 'paused' && (
                      <button
                        onClick={() => {
                          setLoading(true);
                          setActionType('resume');
                          setError(null);
                          setSuccess(null);
                          dispatch(resumeSubscription(subscription.id))
                            .unwrap()
                            .then(() => setSuccess('Subscription resumed successfully.'))
                            .catch(err => setError(err.message || 'Failed to resume subscription.'))
                            .finally(() => setLoading(false));
                        }}
                        disabled={loading && actionType === 'resume'}
                        className="px-6 py-2 bg-green-500 text-white rounded-full font-bold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {loading && actionType === 'resume' && (
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        {loading && actionType === 'resume' ? 'Resuming...' : 'Resume Subscription'}
                      </button>
                    )}

                    {status === 'cancelled' && (
                      <div className="text-gray-500 text-sm">
                        This subscription has been cancelled and cannot be modified.
                      </div>
                    )}
                  </div>
                  <button
                    onClick={onHide}
                    className="px-6 py-2 bg-white text-black rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pause Confirmation Modal */}
      {showPauseConfirm && (
        <div className="fixed inset-0 z-[60] overflow-y-auto" onClick={handleConfirmClose}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"></div>

            {/* Modal panel */}
            <div
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Pause Subscription</h3>
                  <button
                    onClick={handleConfirmClose}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="bg-white px-6 py-4">
                <p className="text-gray-700">
                  You are pausing this subscription. You will not be billed while your subscription is paused. You may unpause at any time.
                </p>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={handleConfirmClose}
                  className="px-6 py-2 bg-white text-black rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePauseConfirm}
                  className="px-6 py-2 bg-yellow-500 text-white rounded-full font-bold hover:bg-yellow-600 transition-colors"
                >
                  Pause Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionDetailModal;
